import { create } from 'zustand';
import { getRouteAndRates, applyUserDiscountToRates } from '../api/pricing';
import { cancelOrder, createOrder, getCurrentOrder, getOrder } from '../api/orders';
import { getPreferences } from '../api/preferences';
import { getUserDiscount } from '../api/discounts';
import { getPointRegion } from '../api/region';
import { reverseGeocode } from '../api/places';
import { getDriverLocation, getNearbyDrivers } from '../api/driver';
import { RealtimeService } from '../api/realtime';
import { getOrderOffers, acceptOffer as acceptOfferApi, rejectOffer as rejectOfferApi } from '../api/offers';
import { config } from '../api/config';
import { ApiError } from '../api/http';
import type {
  CurrentOrder,
  DiscountData,
  LatLon,
  NearbyDriver,
  Offer,
  OrderPreference,
  Rate,
  RegionData,
  RouteAndRates,
  RoutePoint,
} from '../domain/types';
import { type Stage, isActiveOrderStatus, stageFromStatusId } from '../domain/stage';
import { useAuthStore } from './authStore';
import { clearStoredCity, getStoredCity, saveStoredCity, type SupportedCity } from '../domain/cities';
import { requestTelegramLocation } from '../telegram/telegram';

// Mirrors flutter_client: up to 4 route points (1 pickup + up to 3 stops).
// See upsertTempPoint in main_cubit_route.dart.
const MAX_POINTS = 4;

type PointSlot = RoutePoint | null;

type OrderState = {
  stage: Stage;
  selectedCity: SupportedCity | null;
  regionData: RegionData | null;
  points: PointSlot[]; // [pickup, destination, ...stops]; null = empty field
  quote: RouteAndRates | null;
  selectedRate: Rate | null;
  order: CurrentOrder | null;
  loadingQuote: boolean;
  locationStatus: 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported' | 'manual';
  error: string | null;
  availablePreferences: OrderPreference[];
  selectedPreferences: string[]; // ids of selected services
  userDiscount: DiscountData | null;

  // Live driver tracking (SignalR set_driver_location + REST fallback).
  // `driverRaw` is the latest reported driver position; MapView animates the
  // car marker along `driverRouteGeometry` between updates. `followDriver`
  // keeps the camera centered on the car.
  driverRaw: LatLon | null;
  driverRouteGeometry: string | null;
  followDriver: boolean;
  realtimeConnected: boolean;

  // Auction: incoming driver offers during SEARCH_DRIVER. The rider accepts one
  // to assign the driver. `nearbyDrivers` are free cars shown on the map while
  // building a route / searching (ether).
  offers: Offer[];
  acceptingOfferId: string | null;
  nearbyDrivers: NearbyDriver[];

  selectCity: (city: SupportedCity) => Promise<void>;
  changeCity: () => void;
  setPoint: (index: number, point: RoutePoint) => Promise<void>;
  addPoint: () => void;
  removePoint: (index: number) => void;
  requestPickupLocation: () => Promise<void>;
  useManualPickup: () => void;
  selectRate: (rate: Rate) => void;
  fetchQuote: () => Promise<void>;
  confirmOrder: (paymentTypeId?: number) => Promise<void>;
  recoverActiveOrder: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  cancel: () => Promise<void>;
  reset: () => void;
  loadPreferences: () => Promise<void>;
  setSelectedPreferences: (ids: string[]) => void;
  toggleFollowDriver: (follow?: boolean) => void;
  startRealtime: () => Promise<void>;
  stopRealtime: () => void;
  startTracking: (driverId: string) => Promise<void>;
  stopTracking: () => void;
  acceptOffer: (offerId: string) => Promise<void>;
  dismissOffer: (offerId: string) => void;
  syncNearby: () => void;
};

let pollTimer: ReturnType<typeof setInterval> | null = null;

// ── Live driver tracking ──────────────────────────────────────────────────
// One realtime connection per active order (carries offers + driver position),
// plus REST fallback polls for driver coordinates, offers, and nearby cars.
// The socket is the source of truth; REST driver ticks are skipped while it is
// fresh (flutter_client pollCurrentDriverCoordinates shouldSkipFetch gate).
let realtime: RealtimeService | null = null;
let driverPollTimer: ReturnType<typeof setInterval> | null = null;
let offersPollTimer: ReturnType<typeof setInterval> | null = null;
let nearbyPollTimer: ReturnType<typeof setInterval> | null = null;
let trackingDriverId: string | null = null;
let socketFreshAt = 0;
let routeReqInFlight = false;
// Offers auction guards: `acceptedOfferOrderId` blocks re-accept in the window
// between a successful accept and the backend flipping the order status; the
// dismissed set prevents an offer from flickering back before the reject POST
// lands (mirrors flutter_client _dismissedOfferIds).
let acceptedOfferOrderId: string | null = null;
const dismissedOfferIds = new Set<string>();
const SOCKET_STALE_MS = 5_000;
const DRIVER_POLL_MS = 1_500;
const OFFERS_POLL_MS = 3_000;
const NEARBY_POLL_MS = 3_000;

// Stages where the assigned driver's position is relevant to show on the map.
function isTrackingStage(stage: Stage): boolean {
  return (
    stage === 'DRIVER_ON_THE_WAY' ||
    stage === 'DRIVER_IS_WAITING' ||
    stage === 'TRIP_IN_PROGRESS'
  );
}

// Stages where the rider is waiting for/browsing driver offers.
function isSearchStage(stage: Stage): boolean {
  return stage === 'SEARCH_DRIVER' || stage === 'PLANNED_SEARCH_DRIVER';
}

// Stages where free cars (ether) should be shown on the map.
function isPreTripStage(stage: Stage): boolean {
  return (
    stage === 'NO_ORDER' ||
    stage === 'BUILD_ROUTE' ||
    stage === 'TRAVEL_OPTIONS' ||
    isSearchStage(stage)
  );
}

function startOrStopTracking(get: () => OrderState): void {
  const { order, stage } = get();
  if (!order) {
    void get().stopTracking();
    return;
  }
  if (isTrackingStage(stage) && order.driverId) {
    void get().startTracking(order.driverId);
  } else {
    void get().stopTracking();
  }
}

// Ensure the realtime socket is open while an order is active (so offers and
// driver positions stream in), and closed when it ends.
function syncRealtime(get: () => OrderState): void {
  const order = get().order;
  if (order && isActiveOrderStatus(order.statusId)) {
    void get().startRealtime();
  } else {
    void get().stopRealtime();
  }
}

// Start/stop the offers poll based on stage. The realtime new_offer_on_order /
// offer_withdrawn handlers trigger an immediate refetch on top of this 3s poll.
// Leaving the search stage also releases the accept lock.
function syncOffers(get: () => OrderState, set: (p: Partial<OrderState>) => void): void {
  if (isSearchStage(get().stage) && get().order?.id) {
    startOffersPolling(get, set);
  } else {
    stopOffersPolling(set);
    acceptedOfferOrderId = null;
  }
}

async function refetchOffers(get: () => OrderState, set: (p: Partial<OrderState>) => void): Promise<void> {
  const oid = get().order?.id;
  if (!oid) return;
  try {
    const all = await getOrderOffers(oid);
    if (get().order?.id === oid) {
      // Filter out offers the rider already dismissed this session — they may
      // reappear until the reject-offer POST lands server-side.
      set({ offers: dismissedOfferIds.size ? all.filter((o) => !dismissedOfferIds.has(o.id)) : all });
    }
  } catch {
    // transient; next poll retries
  }
}

function startOffersPolling(get: () => OrderState, set: (p: Partial<OrderState>) => void): void {
  // Don't (re)start polling after an offer was accepted for this order — the
  // backend is assigning the driver; the status flip is picked up by the order poll.
  if (offersPollTimer || acceptedOfferOrderId) return;
  void refetchOffers(get, set);
  offersPollTimer = setInterval(() => void refetchOffers(get, set), OFFERS_POLL_MS);
}

function stopOffersPolling(set: (p: Partial<OrderState>) => void): void {
  if (offersPollTimer) {
    clearInterval(offersPollTimer);
    offersPollTimer = null;
  }
  dismissedOfferIds.clear();
  set({ offers: [], acceptingOfferId: null });
}

async function refreshNearby(set: (p: Partial<OrderState>) => void, lat: number, lon: number): Promise<void> {
  try {
    const drivers = await getNearbyDrivers(lat, lon);
    set({ nearbyDrivers: drivers });
  } catch {
    // transient
  }
}

// Fetch driver->pickup road geometry once we know the driver's position, so
// MapView can animate the car along the actual road rather than a straight
// line. Throttled by routeReqInFlight; only needed for the pickup phase.
async function fetchDriverRoute(
  get: () => OrderState,
  set: (p: Partial<OrderState>) => void,
  driverPos: LatLon,
): Promise<void> {
  if (routeReqInFlight) return;
  const pickup = get().points[0];
  if (!pickup) return;
  const phone = useAuthStore.getState().profile?.phone;
  if (!phone) return;
  routeReqInFlight = true;
  try {
    const quote = await getRouteAndRates(
      [{ lat: driverPos.lat, lon: driverPos.lon, address: '' }, pickup],
      phone,
    );
    if (quote.route.geometry) set({ driverRouteGeometry: quote.route.geometry });
  } catch {
    // Non-critical: the car marker still moves on raw positions.
  } finally {
    routeReqInFlight = false;
  }
}

// Apply a driver position from either source. Socket updates mark the socket
// fresh (gating the REST fallback); REST updates do not. The first socket/REST
// position during DRIVER_ON_THE_WAY also triggers the driver->pickup route fetch.
function setDriverPosition(
  get: () => OrderState,
  set: (p: Partial<OrderState>) => void,
  pos: LatLon,
  source: 'socket' | 'rest',
): void {
  if (source === 'socket') socketFreshAt = Date.now();
  set({ driverRaw: pos });
  if (
    get().stage === 'DRIVER_ON_THE_WAY' &&
    !get().driverRouteGeometry &&
    !routeReqInFlight
  ) {
    void fetchDriverRoute(get, set, pos);
  }
}

function regionFromCityFallback(city: SupportedCity): RegionData {
  return {
    countryCode: config.defaultCountry,
    currencyCode: config.defaultCurrency,
    languageCode: config.defaultLanguage.toUpperCase(),
    cityId: city.cityIdFallback ?? '',
    districtId: null,
    h3Code: null,
  };
}

async function resolveRegionForPoint(point: RoutePoint, city?: SupportedCity | null): Promise<RegionData> {
  try {
    const region = await getPointRegion(point);
    if (!region.cityId && city?.cityIdFallback) {
      return { ...region, cityId: city.cityIdFallback };
    }
    return region;
  } catch {
    if (city?.cityIdFallback) {
      return regionFromCityFallback(city);
    }
    throw new Error('Unable to resolve pickup region');
  }
}

async function enrichPickupPoint(point: RoutePoint, city?: SupportedCity | null) {
  const [address, region] = await Promise.all([
    reverseGeocode(point).catch(() => ''),
    resolveRegionForPoint(point, city),
  ]);

  return {
    point: { ...point, address: address || 'Моя геопозиція' },
    region,
  };
}

// Sum of the selected services' prices (flutter_client _calculateServicesPrice).
export function getServicesPrice(
  available: OrderPreference[],
  selected: string[],
): number {
  if (selected.length === 0) return 0;
  let total = 0;
  for (const pref of available) {
    if (selected.includes(pref.id)) total += Math.round(pref.price);
  }
  return total;
}

function filledPoints(points: PointSlot[]): RoutePoint[] {
  return points.filter((p): p is RoutePoint => p !== null);
}

export const useOrderStore = create<OrderState>((set, get) => ({
  stage: 'NO_ORDER',
  selectedCity: getStoredCity(),
  regionData: null,
  points: [null, null],
  quote: null,
  selectedRate: null,
  order: null,
  loadingQuote: false,
  locationStatus: 'idle',
  error: null,
  availablePreferences: [],
  selectedPreferences: [],
  userDiscount: null,
  driverRaw: null,
  driverRouteGeometry: null,
  followDriver: false,
  realtimeConnected: false,
  offers: [],
  acceptingOfferId: null,
  nearbyDrivers: [],

  selectCity: async (city) => {
    saveStoredCity(city);
    set({
      selectedCity: city,
      regionData: null,
      points: [null, null],
      quote: null,
      selectedRate: null,
      locationStatus: 'idle',
      error: null,
    });
    try {
      const region = await getPointRegion(city);
      const regionWithFallback = !region.cityId && city.cityIdFallback
        ? { ...region, cityId: city.cityIdFallback }
        : region;
      set({ regionData: regionWithFallback });
    } catch {
      if (city.cityIdFallback) {
        set({ regionData: regionFromCityFallback(city) });
      }
    }
    void get().loadPreferences();
  },

  changeCity: () => {
    get().stopPolling();
    void get().stopRealtime();
    stopOffersPolling(set);
    acceptedOfferOrderId = null;
    if (nearbyPollTimer) {
      clearInterval(nearbyPollTimer);
      nearbyPollTimer = null;
    }
    get().stopTracking();
    clearStoredCity();
    set({
      selectedCity: null,
      regionData: null,
      points: [null, null],
      quote: null,
      selectedRate: null,
      order: null,
      locationStatus: 'idle',
      error: null,
      stage: 'NO_ORDER',
    });
  },

  setPoint: async (index, point) => {
    const points = [...get().points];
    while (points.length <= index) points.push(null);
    points[index] = point;
    set({ points, quote: null, selectedRate: null });

    // Pickup (index 0) drives region/tariff policy — resolve it like setPointA did.
    if (index === 0) {
      try {
        const region = await resolveRegionForPoint(point, get().selectedCity);
        set({ regionData: region });
      } catch {
        set({ error: 'Не вдалося визначити місто для адреси подачі' });
      }
    }
  },

  addPoint: () => {
    const points = [...get().points];
    if (points.length >= MAX_POINTS) return;
    // Only add a new stop once the last slot is filled (keeps the list tidy).
    const last = points[points.length - 1];
    if (last === null) return;
    points.push(null);
    set({ points });
  },

  removePoint: (index) => {
    if (index < 2) return; // pickup (0) and primary destination (1) are not removable
    const points = [...get().points];
    points.splice(index, 1);
    set({ points, quote: null, selectedRate: null });
  },

  selectRate: (rate) => set({ selectedRate: rate }),

  requestPickupLocation: async () => {
    set({ locationStatus: 'requesting', error: null });

    const applyLocation = async (lat: number, lon: number) => {
      const point = { lat, lon, address: '' };
      const enriched = await enrichPickupPoint(point, get().selectedCity);
      const points = [...get().points];
      points[0] = enriched.point;
      set({
        points,
        regionData: enriched.region,
        locationStatus: 'granted',
      });
    };

    try {
      const telegramLocation = await requestTelegramLocation();
      if (telegramLocation) {
        await applyLocation(telegramLocation.lat, telegramLocation.lon);
        return;
      }
    } catch {
      // Fall back to browser geolocation below.
    }

    if (!navigator.geolocation) {
      set({
        locationStatus: 'manual',
        error: 'Геопозиція недоступна в цьому Telegram. Введіть адресу вручну.',
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await applyLocation(position.coords.latitude, position.coords.longitude);
        } catch {
          set({ locationStatus: 'manual', error: 'Не вдалося визначити адресу автоматично' });
        }
      },
      () => {
        set({
          locationStatus: 'manual',
          error: 'Не вдалося отримати геопозицію. Введіть адресу вручну.',
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  },

  useManualPickup: () => set({ locationStatus: 'manual', error: null }),

  fetchQuote: async () => {
    const pts = filledPoints(get().points);
    if (pts.length < 2) return;
    const phone = useAuthStore.getState().profile?.phone;
    if (!phone) {
      set({ error: 'Не вдалося завантажити телефон профілю. Відкрийте додаток заново.' });
      return;
    }
    set({ loadingQuote: true, error: null });
    try {
      const region = get().regionData?.cityId
        ? get().regionData
        : await resolveRegionForPoint(pts[0], get().selectedCity);

      const quote = await getRouteAndRates(pts, phone);

      // Pull the user's active discount and reflect it in the struck-through price.
      const userDiscount = await getUserDiscount(phone, pts[0]);
      const rates = applyUserDiscountToRates(quote.rates, userDiscount);

      set({
        regionData: region,
        quote: { ...quote, rates },
        selectedRate: rates[0] ?? null,
        userDiscount,
        loadingQuote: false,
        stage: 'TRAVEL_OPTIONS',
      });
    } catch (error) {
      const suffix = error instanceof ApiError ? ` (${error.code ?? error.status})` : '';
      set({ loadingQuote: false, error: `Не вдалося розрахувати маршрут${suffix}` });
    }
  },

  confirmOrder: async (paymentTypeId) => {
    const pts = filledPoints(get().points);
    const { selectedRate, quote, regionData, selectedCity, availablePreferences, selectedPreferences } = get();
    const profile = useAuthStore.getState().profile;
    if (pts.length < 2 || !selectedRate || !quote || !profile) {
      set({ error: 'Недостатньо даних для замовлення' });
      return;
    }
    set({ error: null });
    try {
      const region = regionData?.cityId ? regionData : await resolveRegionForPoint(pts[0], selectedCity);
      if (!region.cityId) {
        set({ error: 'Не вдалося визначити місто для замовлення' });
        return;
      }
      const servicesPrice = getServicesPrice(availablePreferences, selectedPreferences);
      const order = await createOrder({
        profile,
        points: pts,
        rate: selectedRate,
        route: quote.route,
        region,
        paymentTypeId,
        discountType: quote.discountType ?? 0,
        orderPreferences: selectedPreferences,
        servicesPrice,
      });
      set({ order, stage: stageFromStatusId(order.statusId) });
      syncRealtime(get);
      syncOffers(get, set);
      get().startPolling();
    } catch (error) {
      const suffix =
        error instanceof ApiError ? ` (${error.code ?? error.status})` : '';
      set({ error: `Не вдалося створити замовлення${suffix}` });
    }
  },

  recoverActiveOrder: async () => {
    const order = await getCurrentOrder();
    if (order && isActiveOrderStatus(order.statusId)) {
      set({ order, stage: stageFromStatusId(order.statusId) });
      syncRealtime(get);
      syncOffers(get, set);
      startOrStopTracking(get);
      get().startPolling();
    }
  },

  startPolling: () => {
    get().stopPolling();
    pollTimer = setInterval(async () => {
      const current = get().order;
      if (!current) return;
      try {
        const updated = await getOrder(current.id);
        set({ order: updated, stage: stageFromStatusId(updated.statusId) });
        syncRealtime(get);
        syncOffers(get, set);
        startOrStopTracking(get);
        // Once the trip starts, the car follows the order route (A->B), not the
        // driver->pickup approach route. startTracking() early-returns for the
        // same driver, so reconcile the route here on the stage transition.
        if (get().stage === 'TRIP_IN_PROGRESS' && get().quote?.route.geometry) {
          const tripRoute = get().quote!.route.geometry;
          if (get().driverRouteGeometry !== tripRoute) {
            set({ driverRouteGeometry: tripRoute });
          }
        }
        if (!isActiveOrderStatus(updated.statusId)) {
          get().stopPolling();
          void get().stopRealtime();
          stopOffersPolling(set);
        }
      } catch {
        // transient; next tick retries
      }
    }, config.orderPollMs);
  },

  stopPolling: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },

  cancel: async () => {
    const order = get().order;
    if (!order) return;
    try {
      await cancelOrder(order.id);
    } finally {
      get().stopPolling();
      void get().stopRealtime();
      stopOffersPolling(set);
      acceptedOfferOrderId = null;
      if (nearbyPollTimer) {
        clearInterval(nearbyPollTimer);
        nearbyPollTimer = null;
      }
      get().stopTracking();
      get().reset();
    }
  },

  reset: () => {
    get().stopTracking();
    void get().stopRealtime();
    stopOffersPolling(set);
    acceptedOfferOrderId = null;
    if (nearbyPollTimer) {
      clearInterval(nearbyPollTimer);
      nearbyPollTimer = null;
    }
    set({
      stage: 'NO_ORDER',
      points: [null, null],
      quote: null,
      selectedRate: null,
      order: null,
      locationStatus: 'idle',
      error: null,
      selectedPreferences: [],
      driverRaw: null,
      driverRouteGeometry: null,
      followDriver: false,
      offers: [],
      acceptingOfferId: null,
      nearbyDrivers: [],
    });
  },

  loadPreferences: async () => {
    if (get().availablePreferences.length > 0) return;
    try {
      const prefs = await getPreferences();
      set({ availablePreferences: prefs });
    } catch {
      // Non-critical: ordering still works without services.
    }
  },

  setSelectedPreferences: (ids) => set({ selectedPreferences: ids }),

  toggleFollowDriver: (follow) =>
    set({ followDriver: follow ?? !get().followDriver }),

  // Open the realtime socket for the active order. One connection carries
  // both driver offers (new_offer_on_order / offer_withdrawn) and the assigned
  // driver's position (set_driver_location). Idempotent.
  startRealtime: async () => {
    if (realtime) return;
    const pickup = get().points[0];
    realtime = new RealtimeService();
    realtime.setHandlers({
      onDriverLocation: (e) => {
        // Only relevant once a driver is assigned (trackingDriverId set).
        if (!trackingDriverId) return;
        if (e.driverId && e.driverId !== trackingDriverId) return;
        setDriverPosition(get, set, { lat: e.lat, lon: e.lon }, 'socket');
      },
      onNewOffer: (data) => {
        const oid = get().order?.id;
        if (!oid) return;
        if (data['orderId'] && data['orderId'] !== oid) return;
        void refetchOffers(get, set);
      },
      onOfferWithdrawn: (data) => {
        const oid = get().order?.id;
        if (!oid) return;
        if (data['orderId'] && data['orderId'] !== oid) return;
        void refetchOffers(get, set);
      },
      onStateChange: (connected) => set({ realtimeConnected: connected }),
    });
    if (pickup) realtime.updateLocation(pickup.lat, pickup.lon);
    await realtime.start();
  },

  stopRealtime: () => {
    if (realtime) {
      void realtime.stop();
      realtime = null;
    }
    set({ realtimeConnected: false });
  },

  // Begin REST-fallback tracking of the assigned driver. The realtime socket is
  // already open (startRealtime); the REST poll only fires when the socket has
  // been silent past SOCKET_STALE_MS. Idempotent for the same driverId.
  startTracking: async (driverId) => {
    if (trackingDriverId === driverId) return;
    await get().stopTracking();
    trackingDriverId = driverId;
    socketFreshAt = 0;
    routeReqInFlight = false;
    set({
      driverRaw: null,
      driverRouteGeometry: null,
      followDriver: false,
    });

    // TRIP_IN_PROGRESS follows the order route (A->B) we already have; no
    // extra route request needed. DRIVER_ON_THE_WAY fetches driver->pickup
    // once the first position arrives (see setDriverPosition).
    if (get().stage === 'TRIP_IN_PROGRESS' && get().quote?.route.geometry) {
      set({ driverRouteGeometry: get().quote!.route.geometry });
    }

    driverPollTimer = setInterval(async () => {
      if (!trackingDriverId) return;
      if (Date.now() - socketFreshAt < SOCKET_STALE_MS) return; // socket fresh — skip REST tick
      try {
        const pos = await getDriverLocation(trackingDriverId);
        if (pos) setDriverPosition(get, set, pos, 'rest');
      } catch {
        // transient; next tick retries
      }
    }, DRIVER_POLL_MS);
  },

  stopTracking: () => {
    if (driverPollTimer) {
      clearInterval(driverPollTimer);
      driverPollTimer = null;
    }
    trackingDriverId = null;
    socketFreshAt = 0;
    routeReqInFlight = false;
    set({
      driverRaw: null,
      driverRouteGeometry: null,
      followDriver: false,
    });
  },

  // Accept a driver offer -> backend assigns the driver and moves the order to
  // DRIVER_ON_THE_WAY (next poll picks it up). Guards against double-accept:
  // `acceptingOfferId` blocks the UI, `acceptedOfferOrderId` blocks re-accept in
  // the window before the status flips (the offers poll won't restart either).
  acceptOffer: async (offerId) => {
    if (get().acceptingOfferId || acceptedOfferOrderId) return;
    const oid = get().order?.id;
    if (!oid) return;
    set({ acceptingOfferId: offerId, error: null });
    try {
      await acceptOfferApi(oid, offerId);
      acceptedOfferOrderId = oid;
      stopOffersPolling(set);
    } catch (error) {
      const suffix = error instanceof ApiError ? ` (${error.code ?? error.status})` : '';
      set({ acceptingOfferId: null, error: `Не вдалося прийняти пропозицію${suffix}` });
      void refetchOffers(get, set);
    }
  },

  // Dismiss an offer: hide it locally and POST reject-offer (best-effort). The
  // dismissed id is remembered so a race with the next offers poll can't
  // re-show it before the server processes the reject.
  dismissOffer: (offerId) => {
    dismissedOfferIds.add(offerId);
    set({ offers: get().offers.filter((o) => o.id !== offerId) });
    const oid = get().order?.id;
    if (oid) void rejectOfferApi(oid, offerId).catch(() => {});
  },

  // Start/stop the nearby-cars (ether) poll based on stage + pickup. Called
  // from OrderScreen on stage/pickup changes — nearby must run pre-trip, before
  // an order exists, so it can't live in the order poll. Restarts with the
  // current pickup so moving the pin re-queries the right area.
  syncNearby: () => {
    const { stage, points } = get();
    const pickup = points[0];
    if (nearbyPollTimer) {
      clearInterval(nearbyPollTimer);
      nearbyPollTimer = null;
    }
    if (isPreTripStage(stage) && pickup && !(pickup.lat === 0 && pickup.lon === 0)) {
      void refreshNearby(set, pickup.lat, pickup.lon);
      nearbyPollTimer = setInterval(
        () => void refreshNearby(set, pickup.lat, pickup.lon),
        NEARBY_POLL_MS,
      );
    } else {
      set({ nearbyDrivers: [] });
    }
  },
}));
